import React from 'react';
import { fromUnixTime } from 'date-fns';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Components
import FavoriteIcon from '@icons/FavoriteIcon';
import { H4, LabelLarge, LabelSmall, LinkSmall, TagPrimary } from '@styled';

// Functionality
import { useSessionDetailModal } from '@modals/SessionDetailModal';
import { mediaQueries } from '@general/breakpoints';
import colors from '@general/colors';
import { getDateRangeTextLong, getTimeRangeText } from '@general/dates';
import fonts from '@general/fonts';
import { decodeHtml, removeHtmlFromText, truncateText } from '@general/helpers/text.helpers';
import getFavoriteHook from '@hooks/getFavoriteHook';

const Container = styled.div`
    border-bottom: 1px solid ${colors.lightGray};
    padding-bottom: 16px;
    padding-top: 32px;

    @media ${mediaQueries.md} {
        display: flex;
    }
`;

const Column1 = styled.div`
    margin-bottom: 16px;

    @media ${mediaQueries.md} {
        margin-bottom: 0;
        width: 20%;
    }
`;

const Column2 = styled.div`
    padding-right: 32px;
    position: relative;

    @media ${mediaQueries.md} {
        width: 80%;
    }
`;

const Description = styled.p`
    font-family: ${fonts.sans};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 21px;
    margin: 0;

    p,
    ul {
        font-family: ${fonts.sans};
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 21px;
        margin: 0;
    }
`;

const FavoriteIconContainer = styled.div`
    position: absolute;
    right: 0;
    top: 0;
`;

const EventSessionListItem = ({
    id,
    title,
    sessionType,
    sessionStatus,
    sessionStartUnix,
    sessionEndUnix,
    sessionRoom,
    description,
    event,
    isFavorite: initialIsFavorite,
    unfavorited,
    showFavoriteIcon = true,
}) => {
    // -----------------------------
    // State
    // -----------------------------

    const useFavorite = getFavoriteHook({ type: 'session', initialIsFavorite });
    const { isFavorite, isLoadingFavorite, saveFavorite, removeFavorite } = useFavorite();
    const { openModal: openSessionDetailModal } = useSessionDetailModal({
        id,
        title,
        sessionType,
        sessionStatus: sessionStatus?.[0],
        sessionStartUnix,
        sessionEndUnix,
        sessionRoom,
        description,
        event,
        isFavorite,
        showFavoriteIcon,
        toggleFavorite: () => toggleFavorite(),
    });

    // -----------------------------
    // Helpers
    // -----------------------------

    const toggleFavorite = async () => {
        if (isFavorite) {
            await removeFavorite(id);

            if (unfavorited) {
                unfavorited(id);
            }
        } else {
            saveFavorite(id);
        }
    };

    // -----------------------------
    // Render
    // -----------------------------

    return (
        <Container className="event-session-card">
            <Column1>
                <LabelSmall className="d-block mb-2">{getDateRangeTextLong(fromUnixTime(sessionStartUnix))}</LabelSmall>
                <LabelSmall className="d-block">
                    {getTimeRangeText(fromUnixTime(sessionStartUnix), fromUnixTime(sessionEndUnix))}
                </LabelSmall>
            </Column1>

            <Column2>
                {sessionType && sessionType.toUpperCase() !== 'SELECT SESSION TYPE' && (
                    <LabelLarge className="d-block mb-1 text-primary">{sessionType}</LabelLarge>
                )}

                <div className="align-items-center d-flex mb-1rem">
                    <H4>{title}</H4>
                    {sessionStatus?.[0] === 'new' && <TagPrimary className="ml-2">New Session</TagPrimary>}
                    {sessionStatus?.[0] === 'pending' && <TagPrimary className="ml-2">Pending</TagPrimary>}
                </div>

                {sessionRoom && <LabelSmall className="d-block mb-1rem">{sessionRoom}</LabelSmall>}
                {description && (
                    <Description className="mb-1rem">
                        {truncateText(decodeHtml(removeHtmlFromText(description)), 120)}
                    </Description>
                )}

                <LinkSmall
                    href="#"
                    onClick={e => {
                        e.preventDefault();
                        openSessionDetailModal();
                    }}
                >
                    <span>View Session Details</span>
                </LinkSmall>

                {showFavoriteIcon && (
                    <FavoriteIconContainer>
                        <div>
                            <FavoriteIcon
                                id={id}
                                type="session"
                                isLoggedIn
                                isFavorite={isFavorite}
                                isLoading={isLoadingFavorite}
                                tooltipLabel="Session"
                                onClick={toggleFavorite}
                            />
                        </div>
                    </FavoriteIconContainer>
                )}
            </Column2>
        </Container>
    );
};

EventSessionListItem.propTypes = {
    id: PropTypes.number.isRequired,
    title: PropTypes.string,
    sessionType: PropTypes.string,
    sessionStatus: PropTypes.arrayOf(PropTypes.string),
    sessionStartUnix: PropTypes.number,
    sessionEndUnix: PropTypes.number,
    sessionRoom: PropTypes.string,
    description: PropTypes.string,
    isFavorite: PropTypes.bool,
    event: PropTypes.shape({
        //
    }),
    unfavorited: PropTypes.func,
    showFavoriteIcon: PropTypes.bool,
};

export default EventSessionListItem;
